import React from 'react';
import { i18n } from 'config/locales';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import { MilitaryDisciplinaryProceduresItemModel } from 'forms/sections/military/disciplinary/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnError,
  CommonOnUpdate,
  CommonRequired,
  CommonScrollIntoView,
} from '../../../../types/props';
import Comments from 'components/Form/Comments';

interface Props extends Partial<MilitaryDisciplinaryProceduresItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class Procedure extends ValidationElement<Props> {
  update = (queue: Partial<MilitaryDisciplinaryProceduresItemModel>) => {
    this.props.onUpdate({
      Date: this.props.Date,
      Offenses: this.props.Offenses,
      Name: this.props.Name,
      Court: this.props.Court,
      Outcome: this.props.Outcome,
      Comments: this.props.Comments,
      ...queue,
    });
  };

  updateDate = (value: MilitaryDisciplinaryProceduresItemModel['Date']) => {
    this.update({
      Date: value,
    });
  };

  updateOffenses = (value: MilitaryDisciplinaryProceduresItemModel['Offenses']) => {
    this.update({
      Offenses: value,
    });
  };

  updateName = (value: MilitaryDisciplinaryProceduresItemModel['Name']) => {
    this.update({
      Name: value,
    });
  };

  updateCourt = (value: MilitaryDisciplinaryProceduresItemModel['Court']) => {
    this.update({
      Court: value,
    });
  };

  updateOutcome = (value: MilitaryDisciplinaryProceduresItemModel['Outcome']) => {
    this.update({
      Outcome: value,
    });
  };

  updateComments = (value: MilitaryDisciplinaryProceduresItemModel['Comments']) => {
    this.update({
      Comments: value,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="disciplinary-procedure">
        <Field
          title={i18n.t('military.disciplinary.heading.date')}
          help="military.disciplinary.help.date"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            name="Date"
            {...this.props.Date}
            className="procedure-date"
            minDateEqualTo
            hideDay
            maxDate={{ year: new Date().getFullYear() }}
            onUpdate={this.updateDate}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          title={i18n.t('military.disciplinary.heading.offenses')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Offenses`}
        >
          <Textarea
            name="Offenses"
            {...this.props.Offenses}
            className="procedure-offenses"
            onUpdate={this.updateOffenses}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Offenses`}
          />
        </Field>

        <Field
          title={i18n.t('military.disciplinary.heading.name')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Name`}
        >
          <Text
            name="Name"
            {...this.props.Name}
            label={i18n.t('military.disciplinary.label.name')}
            className="procedure-name"
            maxlength="100"
            onUpdate={this.updateName}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Name`}
          />
        </Field>

        <Field
          title={i18n.t('military.disciplinary.heading.court')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Court`}
        >
          <Textarea
            name="Court"
            {...this.props.Court}
            label={i18n.t('military.disciplinary.label.court')}
            className="procedure-court"
            onUpdate={this.updateCourt}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Court`}
          />
        </Field>

        <Field
          title={i18n.t('military.disciplinary.heading.outcome')}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Outcome`}
        >
          <Text
            name="Outcome"
            {...this.props.Outcome}
            label={i18n.t('military.disciplinary.label.outcome')}
            className="procedure-outcome"
            maxlength="100"
            onUpdate={this.updateOutcome}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Outcome`}
          />
        </Field>
        <Comments
          title={i18n.t('military.disciplinary.entrycomments')}
          name="Comments"
          attribute="Comments"
          className="optional-comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          onError={this.props.onError}
          required={false}
        >
        </Comments>
      </div>
    );
  }
}

Procedure.defaultProps = {
  onUpdate: (queue) => { },
  Comments: {},
  onError: (value, arr) => arr,
};
